import { Box, Grid, Typography, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { object, string } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import FormInput from "components/FormInput";
import FormInputPassword from "components/FormInputPassword";
import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LoadingButton as _LoadingButton } from "@mui/lab";
import { useLoginUserMutation } from "redux/api/authApi";
import logo from "assets/logo_verify.svg";
import home from "assets/img-login-admin.jpg";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";



const LinkItem = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.palette.secondary.main};
  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.palette.secondary.dark};
  }
`;

const loginSchema = object({
  email: string()
    .min(1, "El correo electrónico es requerido")
    .email("Debe indicar un correo electrónico"),
  password: string()
    .min(1, "La contraseña es requerida")
    .min(8, "La contraseña debe tener más de 8 caracteres")
    .max(32, "La contraseña debe tener menos de 32 caracteres"),
});

export type LoginInput = {
  email: string;
  password: string;
  general?: {
    message: string;
    type: string;
  };
};

export const LoginPage = () => {
  const { formatMessage } = useIntl();
  const methods = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  });

  const [loginUser, { isLoading, isError, error, isSuccess }] =
    useLoginUserMutation();

  const navigate = useNavigate();
  const location = useLocation();
  const from = ((location.state as any)?.from.pathname as string) || "/";

  const { handleSubmit, setError, clearErrors } = methods;

  useEffect(() => {
    if (isSuccess) {
      navigate(from, { replace: true });
    }
  }, [isSuccess, navigate, from]);

  const onSubmitHandler: SubmitHandler<LoginInput> = async (values) => {
    try {
      clearErrors(); // Limpia errores previos
      await loginUser(values).unwrap();
    } catch (err) {
      setError("password", { message: formatMessage({ id: "login.error.credentials" }) });
    }
  };
  
  //useFormErrorHandling(methods.setError, error);

  return (
    <Grid
      container
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        minHeight: "100%",
        height: "100%"
      }}
    >
      <Grid item md={7} sx={{ 
              height: "100%",
              minHeight: "100%",
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundImage: `url(${home})`,
              display: { xs: "none", md: "flex" } }}>
      </Grid>
      <Grid
        item
        xs={12}
        md={5}
        sx={{
          maxWidth: "27rem",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          minHeight: "100%",
          backgroundImage: `url()`,
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          paddingX: {
            xs: "2rem", // Para pantallas pequeñas (móviles)
            md: "4rem", // Para desktops y pantallas más grandes
          }
        }}
      >
        <Box   sx={{
          maxWidth: "27rem"}}
          display="flex"
          alignItems={"center"} 
          justifyContent={"center"} 
          flexDirection={"column"}
          >
        <Box
          sx={{
            margin: {
              xs: "2rem 1.625rem", // Margen superior para teléfonos móviles
              sm: "2rem", // Margen superior para tablets
              md: "2rem", // Margen superior para desktop
              lg: "1.5rem", // Margen superior para desktop
              xl: "3rem", // Margen superior para desktop
              // Puedes agregar más breakpoints si es necesario
            },
            height: {
              xs: "5.25rem", // Altura para teléfonos móviles
              sm: "80px", // Altura para tablets
              md: "100px", // Altura para desktop
              // Puedes agregar más breakpoints si es necesario
            },
          }}
        >
          <img src={logo} style={{height: '100%', width: 'auto'}} alt="Logo" />
        </Box>
        <Box
          sx={{
            
            marginBottom: {
              xs: "2rem", // Margen superior para teléfonos móviles
              sm: "2rem", // Margen superior para tablets
              md: "3rem", // Margen superior para desktop
              // Puedes agregar más breakpoints si es necesario
            },
            width: {
              xs: "5.625rem", // Altura para teléfonos móviles
              sm: "80px", // Altura para tablets
              md: "100px", // Altura para desktop
              // Puedes agregar más breakpoints si es necesario
            },
          }}
        >
        </Box>
        <Box sx={{ width: "100%", textAlign: "left"}}>
        <Typography
          component="h1"
          color="secondary"
          sx={{
            width: "100%",
            fontWeight: 600,
            fontSize: { xs: "1.5rem", md: "2rem" },
            lineHeight: "1.813rem",
          }}
        >
          {formatMessage({ id: "login.title" })}
        </Typography>
        <Typography
          textAlign="left"
          component="h2"
          sx={{
            width: "100%",
            fontWeight: 400,
            fontSize: { xs: "1rem" },
            marginBottom: "1.2rem",
          }}
        >
          {formatMessage({ id: "login.instructions" })}          
        </Typography>
  
        </Box>  
        <FormProvider {...methods}>
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmitHandler)}
            noValidate
            autoComplete="on" // Set autoComplete to "on" to enable browser autocomplete
            maxWidth="27rem"
            width="100%"
          >
            <FormInput
              name="email"
              label="Correo electrónico"
              type="email"
              autoComplete="email" // Set autoComplete to "email" for email field
            />
            <FormInputPassword
              name="password"
              label="Contraseña"
              autoComplete="current-password" // Set autoComplete to "current-password" for password field
            />
            <Typography
              sx={{ fontSize: "0.9rem", mb: "1rem", textAlign: "left" }}
            >
              <LinkItem
                to="/forgot-password"
                style={{
                  fontWeight: "500",
                  fontSize: ".875rem",
                }}
              >
                {formatMessage({ id: "login.forgot.password" })}
              </LinkItem>
            </Typography>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "1rem",
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                sx={{
                  width: "100%",
                }}
                disableElevation
                type="submit"
              >
                {formatMessage({ id: "login.access" })}
              </Button>
            </Box>
            <Box
              sx={{
                marginBottom: "2.5rem",
              }}
            >
              <Typography
                textAlign="center"
                sx={{
                  marginTop: "1.4rem",
                  color: "#A0AEC0",
                  fontWeight: "600",
                  fontSize: ".875rem",
                  lineHeight: ".946rem",
                }}
              >
                v{process.env.REACT_APP_VERSION}
              </Typography>
            </Box>
            
          </Box>
        </FormProvider>
        </Box>
      </Grid>
    </Grid>
  );
};
